import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import logo from '@/assets/logo.png';
import { api, setAuthToken, setUser } from '@/utils/api';
import { toast } from 'sonner';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login', formData);
      setAuthToken(response.data.access_token);
      setUser(response.data.user);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await api.post('/auth/google', {
        credential: credentialResponse.credential
      });
      setAuthToken(response.data.access_token);
      setUser(response.data.user);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google login failed');
    }
  };

  return (
    <div className="min-h-screen bg-cream grid md:grid-cols-2">
      <div className="bg-sage-600 hidden md:flex flex-col justify-center items-center p-12 text-white">
        <div className="max-w-md">
          <img src={logo} alt="VetCare" className="h-16 w-auto mb-6" />
          <h2 className="text-4xl font-heading font-bold mb-4">Welcome to VetCare</h2>
          <p className="text-lg text-sage-100 mb-8">
            "VetCare has streamlined our entire clinic operation. The team loves how easy it is to use!"
          </p>
          <p className="font-semibold">- Dr. Emily Rodriguez</p>
          <p className="text-sage-200 text-sm">Happy Paws Veterinary Clinic</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center gap-2 mb-4">
                <img src={logo} alt="VetCare" className="h-8 w-auto" />
                <span className="text-2xl font-heading font-bold text-sage-700">VetCare</span>
              </Link>
              <h1 className="text-3xl font-heading font-bold text-sage-800 mb-2">Welcome Back</h1>
              <p className="text-gray-600">Sign in to your clinic account</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-full py-6 bg-sage-500 hover:bg-sage-600 text-white shadow-lg"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="flex justify-center" data-testid="google-login-button">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error('Google login failed')}
                size="large"
                shape="pill"
                theme="outline"
                text="signin_with"
              />
            </div>

            <div className="mt-6 text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-sage-600 font-semibold hover:text-sage-700" data-testid="register-link">
                Create one now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Login = () => (
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <LoginForm />
  </GoogleOAuthProvider>
);
